import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useTransition } from "react";
import { useFormState } from "react-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useLocation } from "@/contexts/location-context";
import { submitWaitlistForm } from "./waitlist-actions";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FormData = z.infer<typeof formSchema>;

interface ServerActionState {
  error?: string;
  success?: boolean;
}

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  city: z.string().min(1, "City is required"), // Changed from location
});

export function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const { toast } = useToast();
  const [isPending, startTransition] = useTransition();
  const [state, formAction] = useFormState<ServerActionState, FormData>(
    submitWaitlistForm,
    { success: false },
  );
  const { searchLocation } = useLocation();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      city: searchLocation || "", // Initial value
    },
  });

  // Update form when location changes
  useEffect(() => {
    form.setValue("city", searchLocation || "");
  }, [searchLocation, form]);

  const onSubmit = (data: FormData) => {
    startTransition(() => {
      formAction(data);
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="rounded-lg border border-black bg-chalk shadow-2xl sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="mb-6 text-center text-3xl font-bold text-black">
            Join the Waitlist
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="firstName"
              rules={{ required: "First name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John"
                      {...field}
                      className="bg-midnightBlue rounded-md border-black/50 placeholder-iceWhite/50 focus:border-black"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              rules={{ required: "Last name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Doe"
                      {...field}
                      className="bg-midnightBlue rounded-md border-black/50 placeholder-iceWhite/50 focus:border-black"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="john.doe@example.com"
                      {...field}
                      className="bg-midnightBlue rounded-md border-black/50 placeholder-iceWhite/50 focus:border-black"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">Location</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your city"
                      {...field}
                      className="bg-midnightBlue rounded-md border-black/50 placeholder-iceWhite/50 focus:border-black"
                    />
                  </FormControl>
                  <FormMessage className="text-red-400" />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="text-darkBlue mt-6 w-full transform rounded-md bg-canary py-3 text-lg font-bold shadow-lg transition-all duration-300 ease-in-out hover:scale-105 hover:bg-goldenrod"
              disabled={isPending}
            >
              {isPending ? "Submitting..." : "Join Waitlist"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
